import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
// Mechanism vocabulary (class → category → term). Same file BiorecipeAPP uses.
import { MECHANISM_TAXONOMY } from "./mechanismTaxonomy";

// ─────────────────────────────────────────────
// FIELD VALUES — taken from the BioRECIPE interaction-list page (BiorecipeAPP.jsx)
// so the Kalimba table offers exactly the same choices.
//
// Differences from BiorecipeAPP, on purpose:
//   • Compartment IDs carry the "GO:" prefix, because that is what the Kalimba
//     backend writes (COMPARTMENT_TO_GO_ID). BiorecipeAPP lists bare numbers.
//   • "Source" is not a dropdown: Kalimba sets it programmatically
//     (LLM / INDRA / ...), so it is left as plain text.
// ─────────────────────────────────────────────
export const MULTI_SELECT_FIELDS = new Set(["Mechanism"]);
export const MULTI_SELECT_GROUPS = { Mechanism: MECHANISM_TAXONOMY };

const ENTITY_TYPES = [
  "",
  "protein",
  "protein complex",
  "gene",
  "RNA",
  "chemical",
  "biological process",
  "protein family",
  "cell",
  "other",
];
const DATABASES = [
  "",
  "UniProt",
  "HGNC",
  "PubChem",
  "Ensembl",
  "GENCODE",
  "RefSeq",
  "Pfam",
  "InterPro",
  "GO",
  "MeSH",
];
const COMPARTMENTS = [
  "",
  "cytoplasm",
  "cytosol",
  "plasma membrane",
  "nucleus",
  "mitochondria",
  "endoplasmic reticulum",
  "extracellular",
];
const COMPARTMENT_IDS = [
  "",
  "GO:0005737",
  "GO:0005829",
  "GO:0005886",
  "GO:0005634",
  "GO:0005739",
  "GO:0005783",
  "GO:0005576",
];

export const DROPDOWN_FIELDS = {
  Sign: ["", "positive", "negative", "unknown", "unaffected", "noDirection"],
  "Regulator Type": ENTITY_TYPES,
  "Regulated Type": ENTITY_TYPES,
  "Regulator Database": DATABASES,
  "Regulated Database": DATABASES,
  "Regulator Compartment": COMPARTMENTS,
  "Regulated Compartment": COMPARTMENTS,
  "Regulator Compartment ID": COMPARTMENT_IDS,
  "Regulated Compartment ID": COMPARTMENT_IDS,
  "Connection Type": ["", "direct", "indirect"],
};

// ─────────────────────────────────────────────
// MULTI-SELECT DROPDOWN (checkbox list, comma-joined value)
// Copied from BiorecipeAPP.jsx without changes in behavior.
// ─────────────────────────────────────────────
const PANEL_W = 360; // grouped picker is wider than its table cell
const PANEL_MAX_H = 380;

export const MultiSelectDropdown = ({
  options = [],
  groups = null, // [{ group, definition, sections: [{ title, terms }] }]
  value,
  onChange,
  defaultOpen = false,
  onClose,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [coords, setCoords] = useState(null);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(() => new Set()); // opened classes
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const searchInputRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const closePanel = useCallback(() => {
    setOpen(false);
    onCloseRef.current?.();
  }, []);

  const updateCoords = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const below = window.innerHeight - rect.bottom - 12;
    const above = rect.top - 12;
    const flip = below < 220 && above > below; // open upwards near the bottom
    const width = groups ? Math.max(rect.width, PANEL_W) : rect.width;
    setCoords({
      top: rect.bottom + 2,
      bottom: window.innerHeight - rect.top + 2,
      left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
      width,
      flip,
      maxH: Math.max(160, Math.min(PANEL_MAX_H, flip ? above : below)),
    });
  }, [groups]);

  useEffect(() => {
    if (!open) return;
    updateCoords();
    setSearch("");
    const focusTimer = setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
    const handleOutside = (e) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target) &&
        panelRef.current &&
        !panelRef.current.contains(e.target)
      ) {
        closePanel();
      }
    };
    // Scroll INSIDE the panel must not close/move it; any other scroll
    // (the table, the page) just re-positions it.
    const handleScroll = (e) => {
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      updateCoords();
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", updateCoords);
    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", updateCoords);
    };
  }, [open, updateCoords, closePanel]);

  const selected = value
    ? value
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];

  const toggleOption = (opt) => {
    let next;
    if (selected.includes(opt)) next = selected.filter((s) => s !== opt);
    else next = [...selected, opt];
    onChange(next.join(", "));
  };

  const q = search.trim().toLowerCase();

  // ── grouped vocabulary ─────────────────────────────────────────────────
  const allTerms = useMemo(
    () =>
      groups
        ? new Set(groups.flatMap((g) => g.sections.flatMap((s) => s.terms)))
        : new Set(options),
    [groups, options],
  );
  const customSelected = selected.filter((t) => !allTerms.has(t));
  const exactMatch =
    q &&
    ([...allTerms].some((t) => t.toLowerCase() === q) ||
      selected.some((t) => t.toLowerCase() === q));

  const visibleGroups = groups
    ? groups
        .map((g) => ({
          g,
          total: new Set(g.sections.flatMap((s) => s.terms)).size,
          picked: new Set(
            g.sections.flatMap((s) => s.terms).filter((t) => selected.includes(t)),
          ).size,
          sections: g.sections
            .map((s) => ({
              title: s.title,
              terms: q
                ? s.terms.filter((t) => t.toLowerCase().includes(q))
                : s.terms,
            }))
            .filter((s) => s.terms.length > 0),
        }))
        .filter((x) => x.sections.length > 0)
    : [];

  const toggleGroup = (name) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  const flatOptions = options
    .filter((o) => o !== "")
    .filter((o) => o.toLowerCase().includes(q));

  const checkRow = (key, term) => (
    <label key={key} className="edit-multiselect-option">
      <input
        type="checkbox"
        checked={selected.includes(term)}
        onChange={() => toggleOption(term)}
      />
      <span style={{ whiteSpace: "normal" }}>{term}</span>
    </label>
  );

  return (
    <div className="edit-multiselect">
      <div
        ref={triggerRef}
        className={`edit-multiselect-display${selected.length === 0 ? " placeholder" : ""}`}
        onClick={() => (open ? closePanel() : setOpen(true))}
        title={selected.join(", ")}
      >
        {selected.length > 0 ? selected.join(", ") : "— select —"}
      </div>
      {open &&
        coords &&
        createPortal(
          <div
            ref={panelRef}
            className="edit-multiselect-panel"
            style={{
              position: "fixed",
              ...(coords.flip
                ? { bottom: coords.bottom }
                : { top: coords.top }),
              left: coords.left,
              width: coords.width,
              maxHeight: coords.maxH,
              overscrollBehavior: "contain",
            }}
          >
            <div className="edit-multiselect-search-wrap">
              <input
                ref={searchInputRef}
                type="text"
                className="edit-multiselect-search"
                placeholder={groups ? "Search all mechanisms..." : "Search..."}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === "Escape") closePanel();
                }}
              />
              {selected.length > 0 && (
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 11,
                    color: "#003594",
                    fontWeight: 600,
                  }}
                >
                  {selected.length} selected
                </div>
              )}
            </div>

            {groups ? (
              <>
                {/* selected terms that are not in the vocabulary (imported / typed) */}
                {customSelected.length > 0 && (
                  <div>
                    <div className="mech-group-title" style={{ cursor: "default" }}>
                      Custom terms
                    </div>
                    {customSelected.map((t) => checkRow(`custom-${t}`, t))}
                  </div>
                )}

                {visibleGroups.map(({ g, total, picked, sections }) => {
                  const isOpen = q ? true : expanded.has(g.group);
                  return (
                    <div key={g.group}>
                      <button
                        type="button"
                        className="mech-group-title"
                        onClick={() => !q && toggleGroup(g.group)}
                        title={g.definition}
                      >
                        <span className="mech-chevron">{isOpen ? "▼" : "▶"}</span>
                        <span style={{ flex: 1, textAlign: "left" }}>
                          {g.group}
                        </span>
                        <span className="mech-count">
                          {picked > 0 ? `${picked} / ${total}` : total}
                        </span>
                      </button>
                      {isOpen &&
                        sections.map((sec) => (
                          <div key={`${g.group}|${sec.title}`}>
                            {sec.title && (
                              <div className="mech-section-title">{sec.title}</div>
                            )}
                            {sec.terms.map((t) =>
                              checkRow(`${g.group}|${sec.title}|${t}`, t),
                            )}
                          </div>
                        ))}
                    </div>
                  );
                })}

                {q && visibleGroups.length === 0 && (
                  <div className="edit-multiselect-empty">No matches</div>
                )}

                {/* the taxonomy is extensible: allow a custom term */}
                {q && !exactMatch && (
                  <div
                    className="edit-multiselect-option"
                    style={{ color: "#003594", fontWeight: 600 }}
                    onClick={() => {
                      toggleOption(search.trim());
                      setSearch("");
                    }}
                  >
                    + Add “{search.trim()}” as a custom term
                  </div>
                )}
              </>
            ) : flatOptions.length === 0 ? (
              <div className="edit-multiselect-empty">No matches</div>
            ) : (
              flatOptions.map((opt) => checkRow(opt, opt))
            )}

            <div
              className="edit-multiselect-option edit-multiselect-clear"
              onClick={() => {
                onChange("");
                closePanel();
              }}
            >
              Clear selection
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

// ─────────────────────────────────────────────
// FIELD EDITOR — the right input for each column
//   Mechanism  → grouped, searchable multi-select
//   Sign, Type, Database, Compartment, Compartment ID, Connection Type → dropdown
//   everything else → text box
// A value that is not in the list (imported or older data) is kept as an
// extra option, so editing a row never silently drops it.
// ─────────────────────────────────────────────
export const FieldEditor = ({
  col,
  value,
  onChange,
  onEnter,
  onEscape,
  autoFocus = false,
}) => {
  if (MULTI_SELECT_FIELDS.has(col)) {
    return (
      <MultiSelectDropdown
        options={DROPDOWN_FIELDS[col] || []}
        groups={MULTI_SELECT_GROUPS[col] || null}
        value={value}
        onChange={onChange}
      />
    );
  }

  const options = DROPDOWN_FIELDS[col];
  if (options) {
    const shown = value && !options.includes(value) ? [...options, value] : options;
    return (
      <select
        className="edit-select"
        value={value}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") onEscape?.();
        }}
      >
        {shown.map((opt) => (
          <option key={opt} value={opt}>
            {opt === "" ? "— select —" : opt}
          </option>
        ))}
      </select>
    );
  }

  return (
    <input
      className="edit-input"
      type="text"
      value={value}
      autoFocus={autoFocus}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") onEnter?.();
        if (e.key === "Escape") onEscape?.();
      }}
    />
  );
};

// ─────────────────────────────────────────────
// STYLES for the editors (append to the page's own styles)
// ─────────────────────────────────────────────
export const fieldEditorStyles = `
.edit-select {
  width: 100%; padding: 3px 6px; font-size: 12px;
  border: 1px solid #003594; border-radius: 4px; outline: none;
  background: #fff; color: #000; cursor: pointer;
}
.edit-select:focus { border-color: #003594; box-shadow: 0 0 0 2px rgba(0,53,148,0.15); }

.edit-multiselect { position: relative; width: 100%; }
.edit-multiselect-display {
  width: 100%; padding: 3px 6px; font-size: 12px;
  border: 1px solid #003594; border-radius: 4px; outline: none;
  background: #fff; color: #000; cursor: pointer; min-height: 24px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.edit-multiselect-display.placeholder { color: #888; }
.edit-multiselect-panel {
  z-index: 9999; background: #fff; border: 1px solid #003594; border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.18); max-height: 220px; overflow-y: auto; padding: 4px 0;
}
.edit-multiselect-search-wrap {
  padding: 4px 8px 6px; position: sticky; top: 0; background: #fff; border-bottom: 1px solid #C8C9C7;
}
.edit-multiselect-search {
  width: 100%; padding: 4px 6px; font-size: 12px; border: 1px solid #66B2E3;
  border-radius: 4px; outline: none; box-sizing: border-box;
}
.edit-multiselect-search:focus { border-color: #003594; box-shadow: 0 0 0 2px rgba(0,53,148,0.15); }
.edit-multiselect-empty { padding: 8px 10px; font-size: 12px; color: #999; font-style: italic; }
.edit-multiselect-option {
  display: flex; align-items: center; gap: 6px; padding: 4px 10px;
  font-size: 12px; cursor: pointer; white-space: nowrap;
}
.edit-multiselect-option:hover { background: #DBEEFF; }
.edit-multiselect-option input { cursor: pointer; }
.edit-multiselect-clear { border-top: 1px solid #C8C9C7; color: #B87333; font-style: italic; }

.mech-group-title {
  display: flex; align-items: center; gap: 6px; width: 100%; padding: 6px 10px;
  border: none; border-top: 1px solid #E5E7EB; background: #F0F4FB; color: #00205B;
  font-size: 12px; font-weight: 700; cursor: pointer; text-align: left;
}
.mech-group-title:hover { background: #DBEEFF; }
.mech-chevron { font-size: 8px; color: #003594; width: 10px; }
.mech-count {
  font-size: 10px; font-weight: 600; color: #fff; background: #66B2E3;
  border-radius: 10px; padding: 1px 7px;
}
.mech-section-title {
  padding: 5px 10px 2px 22px; font-size: 10px; font-weight: 700;
  letter-spacing: 0.06em; text-transform: uppercase; color: #8A8F98;
}
.edit-multiselect-panel .edit-multiselect-option { padding-left: 22px; }
`;
